import { safePathEncode } from '@/utils/safePath'
import { apiSlice } from './apiSlice'

export interface LookupItem {
  value: string
  label: string
  meta?: Record<string, unknown>
}

export interface LookupResponse {
  items: LookupItem[]
  total: number
}

/** Mirrors FastAPI `SaleContractOut` / write payload (camelCase aliases). */
export interface SaleContract {
  id: number
  contractNo: string
  buyerRef: string | null
  contractDate: string
  clientCode: string
  clientShort?: string | null
  clientName?: string | null
  consigneeCode: string | null
  consigneeName?: string | null
  quantity: number
  packagingCode: string | null
  bags: number | null
  qualityCode: string | null
  certificationCode: string | null
  contractType: string
  fixationCall: string | null
  price: number | null
  priceUnit: string | null
  ptbfDiff: number | null
  vatRate: number | null
  vatApplicable?: boolean | null
  futuresMonth: string | null
  fxRate: number | null
  deliveryTerm: string | null
  warehouse: string
  shipmentMonth?: string | null
  shipmentDateFrom: string | null
  shipmentDateTo: string | null
  deliveryMonth?: string | null
  deliveryDateFrom: string | null
  deliveryDateTo: string | null
  isShipDeli: number | null
  pss: string | null
  paymentWeightTerm: string | null
  franchise: number | null
  paymentTerms: string | null
  conditionsCode: string | null
  note: string | null
  freight: number | null
  logisticsCost: number | null
  insuranceCost?: number | null
  weightingCost?: number | null
  financeCost: number | null
  bankCost: number | null
  description: string | null
  isIncludedRoyal?: boolean | null
  royalFee: number | null
  brokerCode: string | null
  brokerRef?: string | null
  brokerFirm?: string | null
  commission: number | null
  traderCode: string | null
  traderName?: string | null
  packaging?: string | null
  packagingEng?: string | null
  futuresMonth2?: string | null
  fxAuto?: number | null
  qualityPremium?: number | null
  certPremium?: number | null
  packagePremium?: number | null
  g2FobDiffBulk?: number | null
  totalLot?: number | null
  lotFixed?: number | null
  lotToFix?: number | null
  avgFuturesPrice?: number | null
  impliedDiffs?: number | null
  kgFixed?: number | null
  kgToFix?: number | null
  includedRoyal?: string | null
  matchingKg?: number | null
  unmatchedKg?: number | null
  avgPrice?: number | null
  deliveredBag?: number | null
  pendingBag?: number | null
  delivered?: number | null
  pending?: number | null
  tolerance?: number | null
  dateUpdate?: string | null
  personUpdate?: string | null
  finalizedDate?: string | null
  sampCountAccept?: number | null
  sampCountReject?: number | null
  sampCountWaiting?: number | null
  vatTxt?: string | null
  ctrlFilldocTxt?: string | null
  sentContract: boolean
  sentPaper: boolean
  receivedContract: boolean
  receivedPaper: boolean
  isStatus?: number | null
  finalizedText?: string | null
  sentContractText?: string | null
  receivedContractText?: string | null
  sentPaperText?: string | null
  receivedPaperText?: string | null
  saleOffshoreOnshore?: string | null
}

export interface SaleContractListResponse {
  items: SaleContract[]
  total: number
  page: number
  pageSize: number
}

export interface SaleContractListQuery {
  page: number
  pageSize: number
  q?: string
  clientCode?: string
  contractType?: string
  certification?: string
  status?: string
}

/** Access MdQry / §8.5.5 certification premium history rows. */
export interface CertificationPremiumChangeRow {
  changedDate: string | null
  premiumValue: number | null
  dateUpdate: string | null
  personUpdate: string | null
}

export interface CertificationPremiumChangeListResponse {
  items: CertificationPremiumChangeRow[]
}

export type SaleReferenceRow = Record<string, unknown>

export interface SaleReferenceRowsQuery {
  pageKey: string
  limit?: number
}

export interface SaleReferencePageResponse {
  pageKey: string
  sourceForm: string
  sourceQuery: string
  items: SaleReferenceRow[]
  total: number
}

export interface CopyFieldAction {
  field: string
  action: string
  source?: string
}

export interface CopyDraftApiResponse {
  sourceContractSale?: string | null
  mode: string
  draft: SaleContractPayload
  brokerFirm?: string | null
  traderName?: string | null
  fieldActions: CopyFieldAction[]
  mustExtractFromAccess?: string[]
}

export interface PrintOffOptionsResponse {
  contractNo: string
  arbitration?: string | null
  conditions?: string | null
  qualityDescEn?: string | null
  conditionsDesc?: string | null
}

export interface PrintOnOptionsResponse {
  contractNo: string
  quality?: string | null
  paymentDescVn?: string | null
  qualityDescVn?: string | null
}

export interface PrintPreviewResponse {
  title: string
  html: string
  message?: string
}

export interface RepInfoItem {
  id: number
  name?: string | null
  position?: string | null
}

export interface RepInfoResponse {
  items: RepInfoItem[]
  currentSignId?: number | null
}

export interface SaleContractPagePreferences {
  items: RepInfoItem[]
  currentSignId?: number | null
  shipmentPeriodFrom: string
  shipmentPeriodTo: string
  finalizedFilter: 'all' | 'yes' | 'no'
}

type SaleReferenceColumnMode =
  | 'ref-ValueUpdates'
  | 'ref-CertificationUpdates'
  | 'ref-Claims'
  | 'ref-Packages'
  | 'ref-ListandValo'
  | 'ref-DeliveryTerms'
  | 'ref-FuturesAllocation'
  | 'ref-CPList'
  | 'ref-Consignee'
  | 'ref-Certification'
  | 'ref-Ex_Calendar'
  | 'ref-DeliveryPlace'
  | 'ref-FuturesLotAdjust'

/** URL mode segment for `GET|PUT /sale-contracts/preferences/columns/{mode}` — khớp backend `sale_contract_column_preference`. */
export type SaleContractColumnsMode =
  | 'sale-list'
  | 'consignment'
  | 'amendment'
  | 'ptbf-fixation'
  | SaleReferenceColumnMode

export interface SaleContractColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

export interface SaleContractListFiltersPatch {
  shipmentPeriodFrom?: string
  shipmentPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export type SaleContractPayload = Omit<
  SaleContract,
  | 'id'
  | 'clientShort'
  | 'clientName'
  | 'consigneeName'
  | 'brokerFirm'
  | 'traderName'
  | 'isStatus'
  | 'finalizedText'
  | 'sentContractText'
  | 'receivedContractText'
  | 'sentPaperText'
  | 'receivedPaperText'
  | 'saleOffshoreOnshore'
>

const saleContractApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleContracts: builder.query<SaleContractListResponse, SaleContractListQuery>({
      query: ({ page, pageSize, q, clientCode, contractType, certification, status }) => ({
        url: '/sale-contracts',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(clientCode ? { clientCode } : {}),
          ...(contractType ? { contractType } : {}),
          ...(certification ? { certification } : {}),
          ...(status ? { status } : {}),
        },
      }),
      providesTags: ['SaleContract'],
    }),

    getSaleContract: builder.query<SaleContract, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [{ type: 'SaleContract', id: contractNo }],
    }),

    createSaleContract: builder.mutation<SaleContract, SaleContractPayload>({
      query: (body) => ({ url: '/sale-contracts', method: 'POST', body }),
      invalidatesTags: ['SaleContract'],
    }),

    updateSaleContract: builder.mutation<SaleContract, { id: number; body: SaleContractPayload }>({
      query: ({ id, body }) => ({ url: `/sale-contracts/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleContract'],
    }),

    cancelSaleContract: builder.mutation<SaleContract, number>({
      query: (id) => ({ url: `/sale-contracts/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['SaleContract'],
    }),

    deleteSaleContract: builder.mutation<SaleContract, number>({
      query: (id) => ({ url: `/sale-contracts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleContract'],
    }),

    /** Email-permissioned hard-delete access (legacy `btnDelete` rule, parity with Purchase). */
    getSaleContractCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-contracts/access/can-delete',
    }),

    suggestContractNo: builder.query<{ contractNo: string }, void>({
      query: () => ({ url: '/sale-contracts/suggest-contract-no' }),
      transformResponse: (r: { suggestedNo: string }) => ({ contractNo: r.suggestedNo }),
    }),

    /** Pre-save check (skill access-suggest): contract_sale đã có active row chưa? */
    checkContractSaleExists: builder.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/sale-contracts/check-contract-no',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),

    getCertificationPremiumChanges: builder.query<CertificationPremiumChangeListResponse, number | void>({
      query: (limit) => ({
        url: '/sale-contracts/certification-premium-changes',
        ...(limit != null ? { params: { limit } } : {}),
      }),
    }),

    getSaleReferenceRows: builder.query<SaleReferencePageResponse, SaleReferenceRowsQuery>({
      query: ({ pageKey, limit = 1000000 }) => ({
        url: `/sale-contracts/references/${encodeURIComponent(pageKey)}`,
        params: { limit },
      }),
      providesTags: (_r, _e, { pageKey }) => [
        { type: 'SaleReference' as const, id: pageKey },
        { type: 'SaleReference' as const, id: 'LIST' },
      ],
    }),

    getRepInfo: builder.query<RepInfoResponse, void>({
      query: () => ({ url: '/sale-contracts/rep-info' }),
      providesTags: ['SaleContract'],
    }),

    getSaleContractPagePreferences: builder.query<SaleContractPagePreferences, void>({
      query: () => ({ url: '/sale-contracts/page-preferences' }),
      providesTags: ['SaleContractPrefs'],
    }),

    getSaleContractColumnsPreference: builder.query<SaleContractColumnsPreference, SaleContractColumnsMode>({
      query: (mode) => ({
        url: `/sale-contracts/preferences/columns/${encodeURIComponent(mode)}`,
      }),
      providesTags: (_r, _e, mode) => [{ type: 'SaleContractColumnPrefs', id: mode }],
    }),

    putSaleContractColumnsPreference: builder.mutation<
      { message: string },
      { mode: SaleContractColumnsMode; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ mode, columns, defaultColumns }) => ({
        url: `/sale-contracts/preferences/columns/${encodeURIComponent(mode)}`,
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: (_r, _e, { mode }) => [{ type: 'SaleContractColumnPrefs', id: mode }],
    }),

    patchSaleContractListFilters: builder.mutation<{ message: string }, SaleContractListFiltersPatch>({
      query: (body) => ({
        url: '/sale-contracts/list-filters',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SaleContract', 'SaleContractPrefs', 'SaleAmendment', 'SalePTBFFixation'],
    }),

    setRepInfo: builder.mutation<{ message: string }, number>({
      query: (signId) => ({
        url: '/sale-contracts/rep-info',
        method: 'POST',
        body: { signId },
      }),
      invalidatesTags: ['SaleContract', 'SaleContractPrefs'],
    }),

    copyDraft: builder.mutation<CopyDraftApiResponse, number>({
      query: (id) => ({ url: `/sale-contracts/${id}/copy-draft`, method: 'POST' }),
    }),

    getPrintOffOptions: builder.query<PrintOffOptionsResponse, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}/print-off/options`,
    }),

    getPrintOnOptions: builder.query<PrintOnOptionsResponse, string>({
      query: (contractNo) => `/sale-contracts/${safePathEncode(contractNo)}/print-on/options`,
    }),

    printOffContract: builder.mutation<PrintPreviewResponse, { id: number; body?: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/sale-contracts/${id}/print-off`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    printOnContract: builder.mutation<PrintPreviewResponse, { id: number; body?: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/sale-contracts/${id}/print-on`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    getLookupClients: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/clients', params: q ? { q } : {} }),
    }),
    getLookupConsignees: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/consignees', params: q ? { q } : {} }),
    }),
    getLookupPackages: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/packages', params: q ? { q } : {} }),
    }),
    getLookupQualities: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/qualities', params: q ? { q } : {} }),
    }),
    getLookupCertifications: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/certifications',
    }),
    getLookupContractTypes: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/contract-types',
    }),
    getLookupFixationCalls: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/fixation-calls',
    }),
    getLookupPriceUnits: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/price-units',
    }),
    getLookupFuturesMonths: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/futures-months',
    }),
    getLookupDeliveryTerms: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/delivery-terms',
    }),
    getLookupPaymentWeightTerms: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/payment-weight-terms',
    }),
    getLookupConditions: builder.query<LookupResponse, void>({
      query: () => '/sale-contracts/lookups/conditions',
    }),
    getLookupBrokers: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/brokers', params: q ? { q } : {} }),
    }),
    getLookupTraders: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/traders', params: q ? { q } : {} }),
    }),
    getLookupArbitrations: builder.query<LookupResponse, string | void>({
      query: (q) => ({ url: '/sale-contracts/lookups/arbitrations', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleContractsQuery,
  useGetSaleContractQuery,
  useCreateSaleContractMutation,
  useUpdateSaleContractMutation,
  useCancelSaleContractMutation,
  useDeleteSaleContractMutation,
  useGetSaleContractCanHardDeleteQuery,
  useSuggestContractNoQuery,
  useLazySuggestContractNoQuery,
  useLazyCheckContractSaleExistsQuery,
  useGetCertificationPremiumChangesQuery,
  useGetSaleReferenceRowsQuery,
  useGetRepInfoQuery,
  useGetSaleContractPagePreferencesQuery,
  useGetSaleContractColumnsPreferenceQuery,
  usePutSaleContractColumnsPreferenceMutation,
  usePatchSaleContractListFiltersMutation,
  useSetRepInfoMutation,
  useCopyDraftMutation,
  useGetPrintOffOptionsQuery,
  useGetPrintOnOptionsQuery,
  usePrintOnContractMutation,
  usePrintOffContractMutation,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupConsigneesQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
  useGetLookupCertificationsQuery,
  useGetLookupContractTypesQuery,
  useGetLookupFixationCallsQuery,
  useGetLookupPriceUnitsQuery,
  useGetLookupFuturesMonthsQuery,
  useGetLookupDeliveryTermsQuery,
  useGetLookupPaymentWeightTermsQuery,
  useGetLookupConditionsQuery,
  useLazyGetLookupBrokersQuery,
  useLazyGetLookupTradersQuery,
  useLazyGetLookupArbitrationsQuery,
  useGetLookupArbitrationsQuery,
} = saleContractApi

export default saleContractApi
